import React, { useMemo, useRef, useState } from 'react';
import { makeStyles, ClickAwayListener } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { FORM_COLORS } from '../constants';
import { SkuOption } from '../types';

const useStyles = makeStyles(() => ({
  wrap: {
    position: 'relative',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    background: FORM_COLORS.white,
    overflow: 'hidden',
    transition: 'border-color .15s',
    '&:focus-within': {
      borderColor: FORM_COLORS.pink,
    },
  },
  searchIcon: {
    padding: '0 10px',
    color: FORM_COLORS.textSecondary,
    fontSize: 18,
  },
  input: {
    flex: 1,
    padding: '7px 10px 7px 0',
    border: 'none',
    fontSize: 13,
    fontFamily: 'inherit',
    color: FORM_COLORS.text,
    outline: 'none',
    background: 'transparent',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: FORM_COLORS.white,
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    boxShadow: '0 4px 14px rgba(0,0,0,.1)',
    zIndex: 10,
    maxHeight: 200,
    overflowY: 'auto',
  },
  option: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'none',
    fontFamily: 'inherit',
    color: FORM_COLORS.text,
    padding: '9px 14px',
    cursor: 'pointer',
    fontSize: 13,
    border: 'none',
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
    transition: 'background .1s',
    '&:hover': {
      background: FORM_COLORS.pinkLight,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  optionSku: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    color: FORM_COLORS.textSecondary,
    marginTop: 2,
  },
  empty: {
    padding: '12px 14px',
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
  },
}));

interface SkuSearchSelectProps {
  value: string;
  products: SkuOption[];
  loading?: boolean;
  onSelect: (option: SkuOption) => void;
}

export const SkuSearchSelect = ({
  value,
  products,
  loading,
  onSelect,
}: SkuSearchSelectProps) => {
  const classes = useStyles();
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return products;
    return products.filter(
      (option) =>
        option.sku.toLowerCase().includes(normalized) ||
        option.name.toLowerCase().includes(normalized),
    );
  }, [query, products]);

  const handleSelect = (option: SkuOption) => {
    setQuery(option.sku);
    onSelect(option);
    setOpen(false);
  };

  const emptyMessage = loading ? 'Cargando productos...' : 'Sin resultados';

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classes.wrap}>
        <div className={classes.inputRow}>
          <SearchIcon className={classes.searchIcon} />
          <input
            ref={inputRef}
            className={classes.input}
            value={query}
            placeholder="Buscar por SKU o nombre de producto..."
            autoComplete="off"
            disabled={loading}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />
        </div>
        {open && (
          <div className={classes.dropdown}>
            {filtered.length === 0 ? (
              <div className={classes.empty}>{emptyMessage}</div>
            ) : (
              filtered.map((option) => (
                <button
                  type="button"
                  key={option.sku}
                  className={classes.option}
                  onClick={() => handleSelect(option)}
                >
                  <div>{option.name}</div>
                  <div className={classes.optionSku}>{option.sku}</div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};
